import React, { useContext, useEffect, useRef, useState } from "react";
import Select, { StylesConfig } from "react-select";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import dayjs from "dayjs";
import { AuthContext } from "../../../../context/authcontext";
import type { LifecycleLabels } from "../../../../utils/lifecycleUtils";
import {
  FilterOption,
  FilterOptionGroup,
  SummaryFilterState,
  emptySummaryFilters,
  countSummaryFilters,
  formatSummaryDateRange,
  getSummaryFilterChips,
  withoutLifecycleStages,
} from "./summaryFilters";
import { useSummaryFilterOptions } from "./useSummaryFilterOptions";
import { DEFAULT_DATE_RANGE_LABEL, defaultDateRange } from "../../Dashboard/filters/DashboardFilterDropdown";
import "../../Dashboard/filters/dashboardFilters.css";

const API_DATE_FORMAT = "YYYY-MM-DD";

type SectionKey =
  | "lifecycles"
  | "stages"
  | "status"
  | "reasons"
  | "teams"
  | "users"
  | "source"
  | "priorities"
  | "products"
  | "currencies"
  | "date";

type MultiKey = "lifecycles" | "stages" | "reasons" | "teams" | "users" | "priorities" | "products" | "currencies";

const selectStyles: StylesConfig<any, any, any> = {
  control: (base) => ({ ...base, minHeight: 32, fontSize: 13, borderColor: "#e2e8f0" }),
  valueContainer: (base) => ({ ...base, padding: "0 6px" }),
  multiValue: (base) => ({ ...base, backgroundColor: "#f2f4f7" }),
  multiValueLabel: (base) => ({ ...base, fontSize: 12, paddingRight: 6 }),
  multiValueRemove: () => ({ display: "none" }),
  clearIndicator: () => ({ display: "none" }),
  option: (base) => ({ ...base, fontSize: 13 }),
  groupHeading: (base) => ({ ...base, fontSize: 11, fontWeight: 600 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const quarterStart = (date: dayjs.Dayjs) => date.month(Math.floor(date.month() / 3) * 3).startOf("month");

const dateRangeSettings = () => ({
  autoUpdateInput: false,
  opens: "left",
  startDate: dayjs(defaultDateRange().startDate).toDate(),
  endDate: dayjs(defaultDateRange().endDate).toDate(),
  locale: { cancelLabel: "Clear" },
  ranges: {
    Today: [dayjs().toDate(), dayjs().toDate()],
    "Last 7 Days": [dayjs().subtract(6, "day").toDate(), dayjs().toDate()],
    "Last 30 Days": [dayjs().subtract(29, "day").toDate(), dayjs().toDate()],
    [DEFAULT_DATE_RANGE_LABEL]: [dayjs(defaultDateRange().startDate).toDate(), dayjs(defaultDateRange().endDate).toDate()],
    "This Month": [dayjs().startOf("month").toDate(), dayjs().endOf("month").toDate()],
    "Last Month": [
      dayjs().subtract(1, "month").startOf("month").toDate(),
      dayjs().subtract(1, "month").endOf("month").toDate(),
    ],
    "This Quarter": [quarterStart(dayjs()).toDate(), dayjs().toDate()],
    "Last Quarter": [
      quarterStart(dayjs()).subtract(3, "month").toDate(),
      quarterStart(dayjs()).subtract(1, "day").toDate(),
    ],
    "This Year": [dayjs().startOf("year").toDate(), dayjs().endOf("year").toDate()],
    "Last Year": [
      dayjs().subtract(1, "year").startOf("year").toDate(),
      dayjs().subtract(1, "year").endOf("year").toDate(),
    ],
  },
});

const truncateText = (text: string, maxLength = 18) =>
  text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

const summarizeList = (options: FilterOption[]) =>
  options.length === 0 ? "" : options.length === 1 ? options[0].label : `${options.length} selected`;

interface SummaryFilterDropdownProps {
  filters: SummaryFilterState;
  onChange: (filters: SummaryFilterState) => void;
  names: LifecycleLabels;
  hideUsers?: boolean;
  loading?: boolean;
}

const SummaryFilterDropdown: React.FC<SummaryFilterDropdownProps> = ({
  filters,
  onChange,
  names,
  hideUsers = false,
  loading = false,
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [expanded, setExpanded] = useState<Set<SectionKey>>(() => new Set<SectionKey>());
  const [pickerSettings] = useState(dateRangeSettings);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const options = useSummaryFilterOptions(selectedTenantId, names, hasOpened, isOpen);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest?.(".daterangepicker") || target.closest?.(".sr-select__menu-portal")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOpen = () => {
    if (!isOpen) {
      setHasOpened(true);
    }
    setIsOpen(!isOpen);
  };

  const toggleSection = (key: SectionKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setMulti = (key: MultiKey, selected: readonly FilterOption[] | null) => {
    const list = selected ? [...selected] : [];
    if (key !== "lifecycles") {
      onChange({ ...filters, [key]: list });
      return;
    }
    const lifecycles = list.map(
      (lifecycle) => options.lifecycleOptions.find((option) => option.value === lifecycle.value) ?? lifecycle
    );
    onChange({
      ...filters,
      lifecycles,
      stages: withoutLifecycleStages(filters.stages, lifecycles),
      users: hideUsers ? [] : filters.users,
    });
  };

  const setStatus = (selected: FilterOption | null) => {
    if (filters.status?.value === selected?.value) return;
    onChange({ ...filters, status: selected, reasons: [] });
  };

  const visibleStageOptions = options.stageOptions.filter((group) =>
    group.options.some((option) => filters.lifecycles.some((lifecycle) => lifecycle.value === option.group))
  );

  const visibleReasonOptions = filters.status ? options.reasonOptionsByStatus[filters.status.value] ?? [] : [];
  const stagesDisabled = !filters.lifecycles.length;
  const reasonsDisabled = !filters.status || (!options.loading.reasons && !visibleReasonOptions.length);
  const reasonsHint = !filters.status ? "Select a status first" : `No reasons for ${filters.status.label}`;

  const resetFilters = () => {
    onChange(emptySummaryFilters);
    setIsOpen(false);
  };

  const activeCount = countSummaryFilters(filters);
  const dateLabel = formatSummaryDateRange(filters.startDate, filters.endDate);
  const defaultRange = defaultDateRange();
  const isDefaultDate = !filters.startDate || !filters.endDate;
  const dateInputValue = isDefaultDate
    ? `${DEFAULT_DATE_RANGE_LABEL} (${formatSummaryDateRange(defaultRange.startDate, defaultRange.endDate)})`
    : dateLabel;

  const sectionHeader = (key: SectionKey, title: string, value: string, disabledHint?: string) => (
    <button
      type="button"
      className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
      onClick={() => toggleSection(key)}
      disabled={Boolean(disabledHint)}
    >
      <span className="small text-dark">
        <strong>{title}</strong>
      </span>
      <span className="d-flex align-items-center">
        {(disabledHint || value) && (
          <span className="small text-muted me-2" title={disabledHint || value}>
            {disabledHint || truncateText(value)}
          </span>
        )}
        <i className={`ti ti-chevron-${expanded.has(key) && !disabledHint ? "up" : "down"} small text-dark`} />
      </span>
    </button>
  );

  const multiSection = (
    key: MultiKey,
    title: string,
    sectionOptions: FilterOption[] | FilterOptionGroup[],
    isLoading: boolean,
    placeholder: string,
    disabledHint?: string
  ) => (
    <div className="border rounded mt-2" key={key}>
      {sectionHeader(key, title, summarizeList(filters[key]), disabledHint)}
      {expanded.has(key) && !disabledHint && (
        <div className="p-2 border-top">
          <Select<FilterOption, true, FilterOptionGroup>
            isMulti
            isClearable={false}
            components={{ ClearIndicator: () => null, MultiValueRemove: () => null }}
            options={sectionOptions as any}
            value={filters[key]}
            onChange={(selected) => setMulti(key, selected)}
            placeholder={placeholder}
            isLoading={isLoading}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            classNamePrefix="sr-select"
            styles={selectStyles}
            noOptionsMessage={() => (isLoading ? "Loading..." : "No options")}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="position-relative db-filter" ref={dropdownRef}>
      <button
        type="button"
        className="btn btn-outline-light shadow dropdown-toggle position-relative"
        onClick={toggleOpen}
      >
        <img src="/assets/img/icons/filter.svg" alt="Filter" className="me-1 db-filter-icon" />
        <span className="me-2">Filter</span>
        {activeCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">Active filters</span>
          </span>
        )}
      </button>

      {isOpen && (
        <div className="db-filter-menu sr-filter-menu" role="dialog" aria-label="Analytics Filters">
          <div className="p-3 pb-0">
            <h6 className="mb-3 d-flex align-items-center">
              <img src="/assets/img/icons/filter.svg" alt="Filter" className="me-1 db-filter-icon" />
              Analytics Filters
            </h6>
          </div>

          <div className="px-3 db-filter-body sr-filter-body">
            {multiSection("lifecycles", "Lifecycle", options.lifecycleOptions, options.loading.stages, "All Lifecycles")}
            {multiSection(
              "stages",
              "Stages",
              visibleStageOptions,
              options.loading.stages,
              "All stages in selected lifecycles",
              stagesDisabled ? "Select a lifecycle first" : undefined
            )}

            <div className="border rounded mt-2">
              {sectionHeader("status", "Status", filters.status?.label ?? "")}
              {expanded.has("status") && (
                <div className="p-2 border-top">
                  <Select<FilterOption, false>
                    options={options.statusOptions}
                    value={filters.status}
                    onChange={(selected) => setStatus(selected ?? null)}
                    placeholder="All Statuses"
                    isClearable={false}
                    components={{ ClearIndicator: () => null }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    classNamePrefix="sr-select"
                    styles={selectStyles}
                  />
                </div>
              )}
            </div>

            {multiSection(
              "reasons",
              "Reason",
              visibleReasonOptions,
              options.loading.reasons,
              `All ${filters.status?.label ?? ""} Reasons`,
              reasonsDisabled ? reasonsHint : undefined
            )}
            {multiSection("teams", "Assigned Team", options.teamOptions, options.loading.teams, "All Teams")}
            {!hideUsers &&
              multiSection("users", "Assigned User", options.userOptions, options.loading.users, "All Users")}

            <div className="border rounded mt-2">
              {sectionHeader("source", "Source", filters.source?.label ?? "")}
              {expanded.has("source") && (
                <div className="p-2 border-top">
                  <Select<FilterOption, false>
                    options={options.sourceOptions}
                    value={filters.source}
                    onChange={(selected) => onChange({ ...filters, source: selected ?? null })}
                    placeholder="All Sources"
                    isClearable={false}
                    components={{ ClearIndicator: () => null }}
                    isLoading={options.loading.sources}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    classNamePrefix="sr-select"
                    styles={selectStyles}
                  />
                </div>
              )}
            </div>

            {multiSection("priorities", "Priority", options.priorityOptions, false, "All Priorities")}
            {multiSection(
              "products",
              "Products & Services",
              options.productOptions,
              options.loading.products,
              "All Products & Services"
            )}
            {multiSection("currencies", "Currency", options.currencyOptions, false, "All Currencies")}

            <div className="border rounded mt-2">
              {sectionHeader("date", "Date Range", isDefaultDate ? DEFAULT_DATE_RANGE_LABEL : dateLabel)}
              {expanded.has("date") && (
                <div className="p-2 border-top">
                  <DateRangePicker
                    initialSettings={pickerSettings}
                    onApply={(_event: any, picker: any) =>
                      onChange({
                        ...filters,
                        startDate: dayjs(picker.startDate.toDate()).format(API_DATE_FORMAT),
                        endDate: dayjs(picker.endDate.toDate()).format(API_DATE_FORMAT),
                      })
                    }
                    onCancel={() => onChange({ ...filters, startDate: null, endDate: null })}
                  >
                    <input
                      type="text"
                      className="form-control form-control-sm db-filter-date"
                      readOnly
                      value={dateInputValue}
                    />
                  </DateRangePicker>
                </div>
              )}
            </div>
          </div>

          <div className="p-3 pt-2 mt-2 border-top">
            <button
              type="button"
              className="btn btn-outline-light btn-sm w-100"
              onClick={resetFilters}
              disabled={loading || activeCount === 0}
            >
              <i className="ti ti-refresh me-1" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const SummaryFilterChips = ({
  filters,
  onChange,
  disabled = false,
  showDefaultDateRange = false,
}: {
  filters: SummaryFilterState;
  onChange: (filters: SummaryFilterState) => void;
  disabled?: boolean;
  showDefaultDateRange?: boolean;
}) => {
  const chips = getSummaryFilterChips(filters);
  const showDefaultChip = showDefaultDateRange && (!filters.startDate || !filters.endDate);
  if (!chips.length && !showDefaultChip) return null;
  const defaultRange = defaultDateRange();
  return (
    <div className="d-flex align-items-center flex-wrap gap-2 mb-4">
      {showDefaultChip && (
        <span className="db-filter-chip db-filter-chip-default" title="Default filter">
          <i className="ti ti-calendar db-filter-chip-icon" />
          <span className="db-filter-chip-label">{DEFAULT_DATE_RANGE_LABEL}</span>
          <span className="db-filter-chip-value">
            {formatSummaryDateRange(defaultRange.startDate, defaultRange.endDate)}
          </span>
        </span>
      )}
      {chips.map((chip) => (
        <span key={chip.key} className="db-filter-chip db-filter-chip-active">
          {chip.label.includes(": ") ? (
            <>
              <span className="db-filter-chip-label">{chip.label.slice(0, chip.label.indexOf(": "))}</span>
              <span className="db-filter-chip-value">{chip.label.slice(chip.label.indexOf(": ") + 2)}</span>
            </>
          ) : (
            <span className="db-filter-chip-value">{chip.label}</span>
          )}
          <button
            type="button"
            aria-label={`Remove ${chip.label}`}
            onClick={() => onChange(chip.next)}
            disabled={disabled}
          >
            <i className="ti ti-x" />
          </button>
        </span>
      ))}
      {chips.length > 0 && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 db-filter-clear"
          onClick={() => onChange(emptySummaryFilters)}
          disabled={disabled}
        >
          Clear all
        </button>
      )}
    </div>
  );
};

export default SummaryFilterDropdown;
